function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import Decimal from 'decimal.js';
import { compact } from 'lodash-es';
import { AccountingInvoiceSyncStatusFailureCodeEnum } from 'anchor-ts-infra/src/dto/accounting-invoice/model';
import { InvoiceDisplayStatus } from 'anchor-scout-common/types';
import { Intent } from "../../infra/access-control";
import { getAccountingDetails } from "../../integrations/Accounting/accounting.utils";
import { invoiceModel, relationshipModel, serviceTemplateModel } from "../../models";
import { InvoiceStatus } from "../../models/invoice";
import { InvoiceOptimisticUpdateType } from "../../providers/InvoiceChangesObservableProvider";
import { Accounting } from "../../types/api.model";
import { getAccountingInvoiceErrorMessage } from "../../utils/getAccountingInvoiceErrorMessage";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export const invoiceSearchParam = 'invoice';
export const relationshipSearchParam = 'relationship';
export const terminatedTooltip = 'The agreement is terminated';
export function getFooter(relationship, invoice) {
  const {
    paymentMethod,
    feeControls
  } = relationship;
  return compact([paymentMethod && invoiceModel.isPending(invoice) && {
    id: 'due',
    text: "The payment will be processed automatically on ".concat(invoiceModel.paymentProcessingDate(invoice), " using a ").concat((paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.type) === 'creditCard' ? 'credit card' : 'bank account', " ending in ").concat(paymentMethod.last4, ".")
  }, !feeControls.coverCreditCardFees && (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.type) === 'creditCard' && {
    id: 'cc',
    text: 'Payments made with a credit card are subject to processing fees.'
  }]);
}
export function getURLWithFullPageInvoice(config) {
  const {
    invoiceId,
    relationshipId,
    pathname,
    search
  } = config;
  const url = new URL(pathname || location.pathname, location.origin);
  const searchParams = new URLSearchParams(search || location.search);
  searchParams.forEach((value, key) => url.searchParams.append(key, value));
  url.searchParams.set(invoiceSearchParam, invoiceId);
  url.searchParams.set(relationshipSearchParam, relationshipId);
  return url.pathname + url.search;
}
function syncRefundCallout(_ref) {
  let {
    accounting,
    syncRefundAction
  } = _ref;
  return {
    id: 'sync-refund',
    intent: 'info',
    text: "Refund is ready to sync with ".concat(getAccountingDetails(accounting).fullName, "."),
    status: 'pending',
    actions: [syncRefundAction]
  };
}
function checkItemServiceTemplateIsLinked(serviceTemplates, item, accounting) {
  const serviceTemplate = item.serviceTemplateId ? serviceTemplates.find(st => st.id === item.serviceTemplateId) : undefined;
  return !serviceTemplate || serviceTemplateModel.integratedId({
    serviceTemplate,
    accounting
  });
}
function isItemLinked(item, serviceTemplates, accounting) {
  return getAccountingDetails(accounting).policy.checkServiceMapping({
    item,
    serviceTemplates,
    accounting
  });
}
const syncAfterFailureCallout = _ref2 => {
  let {
    items,
    contacts,
    accounting,
    integration,
    featureFlags,
    relationship,
    retrySyncAction,
    serviceTemplates,
    mapContactsAction,
    mapServicesAction,
    retrySyncWithoutTimeActivitiesAction
  } = _ref2;
  return data => {
    const isClientContactLinked = getAccountingDetails(accounting).policy.checkContactsMapping({
      contacts,
      relationship,
      accounting
    });
    const allServicesLinked = items.every(item => {
      return featureFlags.SERVICE_TEMPLATE_BUNDLE ? isItemLinked(item, serviceTemplates, accounting) : checkItemServiceTemplateIsLinked(serviceTemplates, item, accounting);
    });
    const failureCode = data.failure.failureCode();
    const failedToMapServices = !allServicesLinked && [AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceServiceNotMapped, AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientAndServiceNotMapped].includes(failureCode);
    const failedToMapContacts = !isClientContactLinked && [AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientNotMapped, AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientAndServiceNotMapped].includes(failureCode);
    const failedToMapTimeActivities = AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrTimeActivityCannotAppliedToInvoice === failureCode;
    const mappingActions = compact([failedToMapContacts && mapContactsAction, failedToMapServices && mapServicesAction, ...(failedToMapTimeActivities ? [retrySyncWithoutTimeActivitiesAction, retrySyncAction] : [])]);
    return {
      id: 'sync-after-failure',
      intent: 'warning',
      text: getAccountingInvoiceErrorMessage({
        invoiceIntegration: integration,
        accounting,
        featureFlags
      }),
      status: 'error',
      actions: mappingActions.length > 0 ? mappingActions : [retrySyncAction]
    };
  };
};
export function getAccountingSectionCallout(params) {
  const {
    accounting,
    integration,
    invoiceStatus,
    numRefunds,
    isZeroAmount,
    syncAction,
    syncPaymentAction
  } = params;
  if (!integration) return null;
  return integration.accept({
    whenPendingSync: () => ({
      id: 'ready-to-sync',
      intent: 'info',
      text: "Invoice is ready to sync with ".concat(getAccountingDetails(accounting).fullName, "."),
      status: 'pending',
      actions: [syncAction]
    }),
    whenRefundSyncFailed: syncAfterFailureCallout(_objectSpread(_objectSpread({}, params), {}, {
      integration
    })),
    whenInvoiceSyncFailed: syncAfterFailureCallout(_objectSpread(_objectSpread({}, params), {}, {
      integration
    })),
    whenPaymentSyncFailed: syncAfterFailureCallout(_objectSpread(_objectSpread({}, params), {}, {
      integration
    })),
    whenInvoiceRecorded: () => {
      if ([InvoiceStatus.PaymentConfirmed, InvoiceStatus.Paid].includes(invoiceStatus) && !isZeroAmount) return {
        id: 'payment-sync-failed',
        intent: 'info',
        text: "Payment is ready to sync with ".concat(getAccountingDetails(accounting).fullName, "."),
        status: 'pending',
        actions: [syncPaymentAction]
      };
      return null;
    },
    whenVoidedRecorded: () => null,
    whenPaymentRecorded: () => {
      if (invoiceStatus === InvoiceStatus.Refunded) return syncRefundCallout(params);
      return null;
    },
    whenRefundRecorded: refundRecorded => {
      if (refundRecorded.numRefunds != null && numRefunds != null && refundRecorded.numRefunds < numRefunds) return syncRefundCallout(params);
      return null;
    }
  });
}
export function makeResumePaymentInvoiceAction(isTerminatedRelationship) {
  return {
    text: 'Resume Payment',
    intent: Intent.InvoiceResumePayment,
    disabled: isTerminatedRelationship,
    tooltipTitle: isTerminatedRelationship ? terminatedTooltip : undefined
  };
}
export const getGenericAccountingTooltip = (accounting, itemName) => /*#__PURE__*/_jsxs(_Fragment, {
  children: ["This line item was mapped in ", getAccountingDetails(accounting).fullName, " to ", /*#__PURE__*/_jsx("b", {
    children: itemName
  })]
});
export const getNotVisibleToClientTooltip = () => 'This indication is not visible to your client.';
export const getXeroTooltip = _ref3 => {
  let {
    accounting,
    itemName,
    testid
  } = _ref3;
  return /*#__PURE__*/_jsxs("div", {
    "data-testid": testid,
    children: [getGenericAccountingTooltip(accounting, itemName), ". ", getNotVisibleToClientTooltip()]
  });
};
export const getItemQBOTooltip = (qbo, testid) => /*#__PURE__*/_jsxs("div", {
  "data-testid": testid,
  children: [getGenericAccountingTooltip(Accounting.QBO, qbo.item.name), qbo.class && /*#__PURE__*/_jsxs(_Fragment, {
    children: [' ', "with the Class ", /*#__PURE__*/_jsx("b", {
      children: qbo.class.name
    })]
  }), ". ", getNotVisibleToClientTooltip()]
});
export function onRequestRefundSuccess(_ref4) {
  let {
    now,
    reason,
    invoice,
    sellerId,
    onRefresh,
    setInvoice
  } = _ref4;
  if (invoiceModel.isPaid(invoice) || invoiceModel.isConfirmed(invoice)) {
    const timestamp = now.getTime();
    setInvoice(invoiceModel.setRefunded(invoice, timestamp, now));
    onRefresh({
      optimisticUpdate: {
        type: InvoiceOptimisticUpdateType.Refunded,
        invoiceId: invoice.id,
        timestamp
      }
    });
  } else {
    setInvoice(invoiceModel.setRefundRequested(invoice, sellerId, reason));
    onRefresh({
      optimisticUpdate: {
        type: InvoiceOptimisticUpdateType.RefundRequested,
        invoiceId: invoice.id,
        userId: sellerId,
        reason
      }
    });
  }
}
export function onRequestPaymentConfirmed(_ref5) {
  let {
    invoice,
    isVendor,
    onRefresh,
    setInvoice
  } = _ref5;
  setInvoice(invoiceModel.setPaymentConfirmed(invoice, isVendor));
  onRefresh({
    optimisticUpdate: {
      type: InvoiceOptimisticUpdateType.PaymentConfirmed,
      invoiceId: invoice.id,
      isVendor
    }
  });
}
export function showRequestPaymentMethodModal(invoice, relationship) {
  return invoiceModel.isPaymentMethodUnset(invoice) && !relationshipModel.isTerminated(relationship) && (invoice.displayStatus === InvoiceDisplayStatus.Overdue || invoice.displayStatus === InvoiceDisplayStatus.Issued);
}
export function getBadgeSkin(status) {
  switch (status) {
    case InvoiceDisplayStatus.Issued:
    case InvoiceDisplayStatus.Voided:
    case InvoiceDisplayStatus.Refunded:
    case InvoiceDisplayStatus.PartiallyRefunded:
      return 'default';
    case InvoiceDisplayStatus.PaymentInitiated:
    case InvoiceDisplayStatus.Paid:
      return 'success';
    case InvoiceDisplayStatus.Overdue:
    case InvoiceDisplayStatus.RefundFailed:
    case InvoiceDisplayStatus.PaymentDisputed:
      return 'error';
  }
}
export const onRequestPartialRefundSuccess = _ref6 => {
  let {
    now,
    reason,
    invoice,
    sellerId,
    refundFees,
    refundAmount,
    onRefresh,
    setInvoice
  } = _ref6;
  if (!invoice.payment) return;
  const refunds = invoice.payment.refunds();
  const isFullyRefunded = refunds && refunds.length > 0 ? new Decimal(refunds[refunds.length - 1].amounts.remainingAmount).div(100).sub(new Decimal(refundAmount)).eq(0) : invoiceModel.rawTotalAmount(invoice).eq(new Decimal(refundAmount));
  if (invoiceModel.isPaid(invoice) || invoiceModel.isConfirmed(invoice) || invoiceModel.isPartiallyRefunded(invoice)) {
    const timestamp = now.getTime();
    setInvoice(isFullyRefunded ? invoiceModel.setRefunded(invoice, timestamp, now) : invoiceModel.setPartiallyRefunded(invoice, {
      timestamp,
      fees: invoice.payment.fees(),
      paymentProcessorType: invoice.payment.paymentProcessorType(),
      reason,
      userId: sellerId,
      refundAmount,
      refundFees,
      rawTotalAmount: invoiceModel.rawTotalAmount(invoice)
    }));
    onRefresh({
      optimisticUpdate: _objectSpread(_objectSpread({}, isFullyRefunded ? {
        type: InvoiceOptimisticUpdateType.Refunded
      } : {
        type: InvoiceOptimisticUpdateType.PartiallyRefunded,
        reason,
        refundAmount,
        refundFees,
        fees: invoice.payment.fees(),
        paymentProcessorType: invoice.payment.paymentProcessorType(),
        userId: sellerId,
        rawTotalAmount: invoiceModel.rawTotalAmount(invoice)
      }), {}, {
        invoiceId: invoice.id,
        timestamp
      })
    });
  } else {
    setInvoice(invoiceModel.setPartialRefundRequested({
      invoice,
      userId: sellerId,
      reason,
      refundAmount,
      refundFees
    }));
    onRefresh({
      optimisticUpdate: {
        type: isFullyRefunded ? InvoiceOptimisticUpdateType.RefundRequested : InvoiceOptimisticUpdateType.PartialRefundRequested,
        invoiceId: invoice.id,
        userId: sellerId,
        refundAmount,
        refundFees,
        reason
      }
    });
  }
};
export const getServiceTemplateIdsV1 = invoice => {
  return compact(invoice.items.map(item => item.serviceTemplateId));
};